/*
 * CT Featured Videos
 *
 * Desktop shows one large "spotlight" video with the rest as thumbnails; mobile
 * is a CSS scroll-snap carousel. The layout switch is pure CSS, so this
 * component only handles what CSS cannot: keeping thumbnail previews still, the
 * mobile position dots, and the lightbox. Every tile opens the lightbox — the
 * hero is fixed and thumbnails never swap into it.
 *
 * The Vimeo/YouTube iframe is created on click and destroyed on close, so no
 * third-party player is loaded (or given a cookie) until someone asks for one.
 */
const CT_FV_DESKTOP = '(min-width: 769px)';

class CtFeaturedVideos extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.ct-fv__list');
    this.dots = this.querySelector('.ct-fv__dots');
    this.hero = this.querySelector('.ct-fv__hero');
    this.lightbox = this.querySelector('.ct-fv__lightbox');
    this.stage = this.querySelector('.ct-fv__stage');
    this.closeButton = this.querySelector('.ct-fv__close');
    this.cards = Array.from(this.querySelectorAll('.ct-fv__card'));
    this.autoplayPreviews = this.dataset.autoplayPreviews === 'true';
    this.lastFocused = null;

    this.desktop = window.matchMedia(CT_FV_DESKTOP);

    this.bindEvents();
    this.buildDots();
    this.syncPreviews();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    if (this.desktop && this.onBreakpoint) {
      if (this.desktop.removeEventListener) {
        this.desktop.removeEventListener('change', this.onBreakpoint);
      } else if (this.desktop.removeListener) {
        this.desktop.removeListener(this.onBreakpoint);
      }
    }
    document.body.classList.remove('ct-fv-lightbox-open');
  }

  bindEvents() {
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    };

    this.onBreakpoint = () => this.syncPreviews();

    // Every tile — hero or thumbnail — opens the lightbox. Delegated so the
    // handler does not care which one was hit.
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.ct-fv__media');
      if (!trigger || !this.contains(trigger)) return;
      this.open(trigger);
    });

    // Thumbnails hold a still frame; hovering previews one without the whole
    // row animating at once.
    this.cards.forEach((card) => {
      card.addEventListener('mouseenter', () => {
        if (this.isSpotlight()) this.playPreviews(card, true);
      });
      card.addEventListener('mouseleave', () => {
        if (this.isSpotlight()) this.playPreviews(card, false);
      });
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.lightbox) {
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.close();
      });
    }

    if (this.desktop.addEventListener) {
      this.desktop.addEventListener('change', this.onBreakpoint);
    } else if (this.desktop.addListener) {
      this.desktop.addListener(this.onBreakpoint);
    }

    document.addEventListener('keydown', this.onKeydown);
    this.bindSteps();
  }

  /* Spotlight -------------------------------------------------------- */

  isSpotlight() {
    return Boolean(this.hero) && this.desktop.matches;
  }

  /* Only the hero loops; thumbnails sit on their poster frame until hovered. */
  syncPreviews() {
    const spotlight = this.isSpotlight();

    // The spotlit video is hidden from the row entirely, so in this layout
    // every visible thumbnail is still.
    this.cards.forEach((card) => {
      this.playPreviews(card, !spotlight && this.autoplayPreviews);
    });

    // The hero is hidden below the desktop breakpoint — no point decoding it.
    if (this.hero) {
      this.playPreviews(this.hero, spotlight && this.autoplayPreviews);
    }
  }

  playPreviews(card, shouldPlay) {
    card.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      if (shouldPlay) {
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(function () {});
      } else {
        // Dropping the attribute as well as pausing, so a preview that has not
        // buffered yet does not start on its own once it is ready.
        video.removeAttribute('autoplay');
        video.pause();
      }
    });
  }

  /* Step dropdowns --------------------------------------------------- */

  /* `<details name>` gives exclusive accordions natively; this covers browsers
     that ignore the attribute. */
  bindSteps() {
    const steps = Array.from(this.querySelectorAll('.ct-fv__step-details'));
    if (steps.length < 2) return;

    const supportsExclusive = 'name' in document.createElement('details');
    if (supportsExclusive) return;

    steps.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        steps.forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
  }

  /* Lightbox --------------------------------------------------------- */

  isOpen() {
    return this.lightbox && this.lightbox.classList.contains('is-open');
  }

  open(trigger) {
    if (!this.lightbox || !this.stage) return;

    const scope = trigger.closest('.ct-fv__card') || trigger.parentElement;
    const nativeTemplate = scope ? scope.querySelector('template.ct-fv__native') : null;
    const embedUrl = trigger.dataset.embedUrl;

    this.stage.innerHTML = '';

    if (embedUrl) {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', embedUrl);
      iframe.setAttribute('title', trigger.dataset.videoTitle || 'Video');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('loading', 'eager');
      this.stage.appendChild(iframe);
    } else if (nativeTemplate) {
      this.stage.appendChild(nativeTemplate.content.cloneNode(true));
      const video = this.stage.querySelector('video');
      if (video) {
        video.controls = true;
        video.muted = false;
        video.play().catch(function () {});
      }
    } else {
      return;
    }

    this.lastFocused = document.activeElement;
    this.lightbox.classList.add('is-open');
    this.lightbox.removeAttribute('aria-hidden');
    document.body.classList.add('ct-fv-lightbox-open');
    if (this.closeButton) this.closeButton.focus();
  }

  close() {
    if (!this.isOpen()) return;

    this.stage.innerHTML = ''; // removing the iframe/video is what stops playback
    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ct-fv-lightbox-open');

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  }

  /* Mobile dots ------------------------------------------------------ */

  buildDots() {
    if (!this.list || !this.dots) return;

    const cards = this.cards;
    if (cards.length < 2) return;

    // Positions are measured against the first card rather than the container
    // so the container's scroll padding does not skew the offsets.
    const origin = cards[0];

    cards.forEach((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ct-fv__dot';
      dot.setAttribute('aria-label', 'Go to video ' + (index + 1));
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');

      const mark = document.createElement('span');
      mark.className = 'ct-fv__dot-mark';
      dot.appendChild(mark);

      dot.addEventListener('click', () => {
        this.list.scrollTo({ left: card.offsetLeft - origin.offsetLeft, behavior: 'smooth' });
      });
      this.dots.appendChild(dot);
    });

    this.dots.classList.add('ct-fv__dots--active');

    let frame = null;
    this.list.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          this.syncDots(cards);
        });
      },
      { passive: true }
    );
  }

  syncDots(cards) {
    const scrollLeft = this.list.scrollLeft;
    const origin = cards[0].offsetLeft;
    let closest = 0;
    let smallest = Infinity;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft - origin - scrollLeft);
      if (distance < smallest) {
        smallest = distance;
        closest = index;
      }
    });

    Array.from(this.dots.children).forEach((dot, index) => {
      dot.setAttribute('aria-current', index === closest ? 'true' : 'false');
    });
  }
}

if (!customElements.get('ct-featured-videos')) {
  customElements.define('ct-featured-videos', CtFeaturedVideos);
}
